import { PROPERTY_STATUS } from '../../browse'
import type { usePropertyDetailQuery } from '../api/usePropertyDetailQuery'

// Pure view-model builders for the property-detail page. No Vue reactivity —
// each function takes the parsed detail data (or null) and returns the shape
// the matching template block consumes. Lifted out of usePropertyDetailViewModel
// so the composable body stays small and these stay testable in isolation.

const PERCENT_BASE = 100
const DEFAULT_MIN_INVESTMENT = 1000
const DEFAULT_TOKEN_PRICE = 100
// eslint-disable-next-line no-magic-numbers -- the multipliers ARE the named values
const PRESET_AMOUNT_MULTIPLIERS = [1, 5, 10, 25] as const
const PRESET_ROUNDING_STEP = 100_000

export const PROPERTY_PLACEHOLDER_IMAGE =
  'https://placehold.co/800x500/e9eaeb/717680?text=Property+Image'
const PROPERTY_PLACEHOLDER_THUMB = 'https://placehold.co/400x300/e9eaeb/717680?text=Image'

const numberFormatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 })
const formatRaw = (value: number | null) => (value != null ? numberFormatter.format(value) : '-')

// Several financials fields arrive as `string | null` on the wire (e.g.
// platformFeePercentage, netYieldPercentage) and are displayed as strings
// elsewhere. When a numeric consumer needs them, coerce safely here — a
// non-numeric or null value falls back to 0 rather than poisoning arithmetic
// with NaN (the cause of "AED NaN" in the investment calculator's total cost).
const toNumberOrZero = (value: string | number | null | undefined): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

// Some descriptions arrive with backslash-escaped percent signs / apostrophes
// (LaTeX-style "5\%", "world\'s") and stray whitespace runs. Mirrors the
// marketplace parser's sanitiser so the detail surface doesn't print API
// quirks as visible typos.
const sanitiseDescription = (raw: string | null | undefined): string => {
  const trimmed = raw?.trim()
  if (!trimmed) return '-'
  return trimmed.replace(/\\%/g, '%').replace(/\\'/g, "'").replace(/\s+/g, ' ')
}
const formatPercentageOnly = (value: number | null) => (value != null ? `${value}%` : '-')

// `sanitiseDescription` returns '-' when its input is empty/null — fine for
// section bodies but unsuitable for an optional one-line caption. This helper
// only sanitises when the value is present, otherwise yields an empty string.
const sanitiseOptional = (raw: string | null | undefined): string => {
  const cleaned = sanitiseDescription(raw)
  return cleaned === '-' ? '' : cleaned
}

// Title-case label rows so lowercase API enum strings ("swimming") read as
// proper amenity names ("Swimming") on a premium property surface.
const titleCase = (raw: string): string =>
  raw
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')

export type PropertyDetailData = NonNullable<
  ReturnType<typeof usePropertyDetailQuery>['data']['value']
>

// Many `data?.x ?? fallback` lookups trip the cyclomatic-complexity lint
// but there's no control flow here — just one fallback per field.
/* eslint-disable complexity */

export const buildGallery = (data: PropertyDetailData | null) => {
  const sortedImages = (data?.media ?? [])
    .filter((media) => media.filePath)
    .sort((left, right) => (left.displayOrder ?? 0) - (right.displayOrder ?? 0))
    .map((media) => media.filePath as string)

  return {
    mainImage: sortedImages[0] ?? PROPERTY_PLACEHOLDER_IMAGE,
    thumbnails: [
      sortedImages[1] ?? PROPERTY_PLACEHOLDER_THUMB,
      sortedImages[2] ?? PROPERTY_PLACEHOLDER_THUMB,
    ] as [string, string],
    allImages: sortedImages.length > 0 ? sortedImages : [PROPERTY_PLACEHOLDER_IMAGE],
    closingLabel: data?.daysToClose != null ? `Closes in ${data.daysToClose} days` : undefined,
  }
}

const formatClosingChipLabel = (
  status: string | undefined,
  daysToClose: number | null | undefined,
  daysToLaunch: number | null | undefined,
): { label: string; icon: 'clock' | 'calendar'; urgent: boolean } | undefined => {
  const CLOSING_URGENT_DAYS = 7
  if (status === PROPERTY_STATUS.LIVE && daysToClose != null) {
    return {
      label: `Closes in ${daysToClose} ${daysToClose === 1 ? 'day' : 'days'}`,
      icon: 'clock',
      urgent: daysToClose <= CLOSING_URGENT_DAYS,
    }
  }
  if (status === PROPERTY_STATUS.LAUNCHING_SOON && daysToLaunch != null) {
    return {
      label: `Launches in ${daysToLaunch} ${daysToLaunch === 1 ? 'day' : 'days'}`,
      icon: 'calendar',
      urgent: false,
    }
  }
  return undefined
}

export const buildHeader = (data: PropertyDetailData | null) => {
  const locationParts = [data?.addressLine1, data?.areaName, data?.city].filter(Boolean)
  return {
    location: locationParts.length ? locationParts.join(', ') : '-',
    title: data?.offeringTitle ?? data?.propertyName ?? '-',
    specs: {
      area: data?.builtUpAreaSqm != null ? `${data.builtUpAreaSqm} sqm` : '-',
      propertyType: data?.propertyType
        ? data.propertyType.charAt(0).toUpperCase() + data.propertyType.slice(1)
        : '-',
      bedrooms: data?.numberOfBedrooms ?? 0,
      bathrooms: data?.numberOfBathrooms ?? 0,
    },
    description: sanitiseDescription(data?.propertyDescription ?? data?.offeringDescription),
    statusChip: formatClosingChipLabel(data?.propertyStatus, data?.daysToClose, data?.daysToLaunch),
  }
}

const computePriceChangePct = (listingPrice: number | null, currentValue: number | null) => {
  if (!listingPrice || !currentValue || listingPrice <= 0) return null
  return ((currentValue - listingPrice) / listingPrice) * PERCENT_BASE
}

const formatPriceChangePct = (pct: number | null) => {
  if (pct == null) return null
  return `${pct > 0 ? '+' : ''}${pct.toFixed(2)}`
}

export const buildFinancials = (data: PropertyDetailData | null) => {
  const listingPrice = data?.listingPrice ?? null
  // currentMarketValue is a pre-formatted server string — parse to number only
  // for the price-change computation; display the raw string directly.
  const currentValueStr = data?.financials?.currentMarketValue ?? null
  const currentValueNum = currentValueStr != null ? Number(currentValueStr) : null
  const priceChangePct = computePriceChangePct(
    listingPrice,
    Number.isFinite(currentValueNum) ? currentValueNum : null,
  )
  // Annual yield: prefer the numeric expectedRentalYield on the main object;
  // fall back to the pre-formatted string from financials.
  const annualYield =
    data?.expectedRentalYield != null
      ? formatPercentageOnly(data.expectedRentalYield)
      : (data?.financials?.netYieldPercentage ?? '-')
  return {
    propertyStatus: data?.propertyStatus ?? PROPERTY_STATUS.LIVE,
    targetRoi: formatPercentageOnly(data?.targetRoi5yr ?? null),
    annualYield,
    capitalAppreciation: formatPercentageOnly(data?.expectedCapitalAppreciation ?? null),
    minInvestment: formatRaw(data?.minInvestmentAmount ?? null),
    fundedPercent: data != null ? data?.fundedPercentage : 0,
    available: formatRaw(data?.availableAmount ?? null),
    totalRaised: formatRaw(data?.totalRaised ?? null),
    closingWarning: data?.daysToClose != null ? `Closes in ${data.daysToClose} days` : undefined,
    daysToClose: data?.daysToClose ?? null,
    listingPrice: formatRaw(listingPrice),
    currentMarketValue: currentValueStr ?? '-',
    priceChangePercent: formatPriceChangePct(priceChangePct),
    pricePerToken: formatRaw(data?.tokenPrice ?? null),
    totalTokens:
      data?.totalTokens != null ? new Intl.NumberFormat('en-US').format(data.totalTokens) : '-',
    grossYield: data?.financials?.grossYieldPercentage ?? '-',
    daysToLaunch: data?.daysToLaunch ?? null,
    launchDate: data?.investmentWindowStart ?? null,
  }
}

type Financials = PropertyDetailData['financials']

export const buildFinancialSections = (financials: Financials | null | undefined) => [
  {
    icon: 'money_bag',
    label: 'Income Breakdown',
    items: [
      { label: 'Annual Rental Income', value: financials?.annualRentalIncome ?? '-' },
      { label: 'Gross Yield', value: financials?.grossYieldPercentage ?? '-' },
      { label: 'Net Yield', value: financials?.netYieldPercentage ?? '-' },
      { label: 'Current Market Value', value: financials?.currentMarketValue ?? '-' },
    ],
  },
  {
    icon: 'percent_discount',
    label: 'Property Costs',
    items: [
      { label: 'Annual Service Charge', value: financials?.annualServiceChargeTotal ?? '-' },
      { label: 'Property Management', value: financials?.propertyManagementFeePct ?? '-' },
      { label: 'Insurance (Annual)', value: financials?.insuranceCostAnnual ?? '-' },
    ],
  },
  {
    icon: 'poker_chip',
    label: 'Fees',
    items: [
      { label: 'Acquisition Fee', value: financials?.acquisitionFee ?? '-' },
      { label: 'DLD Fee', value: financials?.dldFee ?? '-' },
      { label: 'Tokenisation Fee', value: financials?.tokenisationFee ?? '-' },
      {
        label: 'Platform Fee',
        value:
          [
            financials?.platformFeePercentage,
            financials?.platformFeeAmount ? `(${financials.platformFeeAmount})` : null,
          ]
            .filter(Boolean)
            .join(' ') || '-',
      },
      { label: 'Exit Fee', value: financials?.exitFeePercentage ?? '-' },
    ],
  },
]

export const buildAbout = (data: PropertyDetailData | null) => {
  const locationParts = [data?.areaName, data?.city].filter(Boolean)
  return {
    description: sanitiseDescription(data?.propertyDescription ?? data?.offeringDescription),
    developerName: data?.developerName ?? '-',
    developerTagline: locationParts.length ? locationParts.join(', ') : '-',
  }
}

export const buildAmenities = (data: PropertyDetailData | null) =>
  (data?.amenities ?? [])
    .filter((amenity): amenity is typeof amenity & { name: string } => Boolean(amenity.name))
    .map((amenity) => ({
      // `icon` is an admin-configured image URL (feature pending). Until then the
      // API serves icon-name strings or null — both are non-URLs, so we pass the
      // raw value through and let the component fall back to a kit icon when it
      // isn't a loadable image. Never fake a `pi-*` class here: it's not a URL.
      icon: amenity.icon ?? '',
      label: titleCase(amenity.name),
    }))

export const buildHighlights = (data: PropertyDetailData | null) =>
  (data?.highlights ?? [])
    .filter(
      (highlight): highlight is typeof highlight & { highlightText: string } =>
        Boolean(highlight.highlightText) && highlight.highlightText !== 'string',
    )
    .sort((left, right) => (left.displayOrder ?? 0) - (right.displayOrder ?? 0))
    .map((highlight) => ({
      icon: highlight.highlightIcon ?? '',
      title: sanitiseOptional(highlight.highlightText) || highlight.highlightText,
      description: sanitiseOptional(highlight.highlightBrief),
    }))

const roundToPresetStep = (amount: number, isBaseline: boolean) =>
  isBaseline ? amount : Math.floor(amount / PRESET_ROUNDING_STEP) * PRESET_ROUNDING_STEP

export const buildCalculator = (data: PropertyDetailData | null) => {
  const min = data?.minInvestmentAmount ?? DEFAULT_MIN_INVESTMENT
  const tokenPrice = data?.tokenPrice ?? DEFAULT_TOKEN_PRICE
  const minTokens = tokenPrice > 0 ? Math.ceil(min / tokenPrice) : 1
  return {
    presetAmounts: PRESET_AMOUNT_MULTIPLIERS.map((multiplier, index) =>
      roundToPresetStep(min * multiplier, index === 0),
    ),
    defaultAmount: min,
    annualYieldPercent:
      data?.expectedRentalYield ?? toNumberOrZero(data?.financials?.netYieldPercentage),
    estimatedRoiPercent: data?.targetRoi5yr ?? 0,
    tokenPrice,
    feePercent: toNumberOrZero(data?.financials?.platformFeePercentage),
    tokenAvailable: data?.tokensAvailable ?? 0,
    minTokens,
    totalTokens: data?.totalTokens ?? 0,
  }
}

export const buildDocuments = (data: PropertyDetailData | null) =>
  (data?.documents ?? [])
    .filter((document): document is typeof document & { filePath: string } =>
      Boolean(document.filePath),
    )
    .map((document) => ({
      id: document.id ?? document.filePath,
      title: document.documentTitle ?? document.fileName ?? 'Document',
      fileUrl: document.filePath,
      fileName: document.fileName ?? 'document',
      mimeType: document.mimeType ?? 'application/octet-stream',
      documentType: document.documentType ?? '',
    }))

export const buildLocation = (data: PropertyDetailData | null) => {
  const addressParts = [data?.addressLine1, data?.areaName, data?.city].filter(Boolean)
  const neighborhoodParts = [data?.areaName, data?.city].filter(Boolean)
  return {
    address: addressParts.length ? addressParts.join(', ') : null,
    neighborhood: neighborhoodParts.length ? neighborhoodParts.join(' · ') : null,
    lat: data?.latitude ?? null,
    lng: data?.longitude ?? null,
    googleMapsUrl: data?.googleMapsUrl ?? null,
  }
}

/* eslint-enable complexity */
